/**
 * <ss-picker> — the segmented control used by the material picker and the
 * contact form's topic row.
 *
 * Two jobs, either of which is a no-op when its markup is absent:
 *
 *   1. Readout. Each button carries data-<field> attributes; pressing one
 *      copies them into the matching [data-field="<field>"] inside the same
 *      picker. Liquid server-renders the first option's values, so the readout
 *      is already correct and complete before this file loads — and
 *      `ss-picker:not(:defined) .ss-seg { display: none }` keeps the buttons
 *      hidden until it does. Nothing here is load-bearing for a no-JS visitor.
 *
 *   2. Hidden input. The pressed button's data-value is written into
 *      [data-ss-picker-input], which is how the contact form submits a topic.
 *      That input has to live inside the {% form %}, so the element wraps
 *      content that is itself inside the form rather than wrapping the form.
 *
 * aria-pressed toggle semantics are used rather than role="radiogroup", which
 * would need a roving tabindex and would discard the [aria-pressed='true']
 * style hook sneakersalt.css already defines for .ss-btn--ghost. Every button
 * stays tabbable; arrow keys are added on top because the control *looks* like
 * a segmented control and people try them.
 */
class SSPicker extends HTMLElement {
  connectedCallback() {
    this.group = this.querySelector('[role="group"]');
    if (!this.group) return;

    this.buttons = Array.from(this.group.querySelectorAll('button'));
    if (this.buttons.length === 0) return;

    this.input = this.querySelector('[data-ss-picker-input]');

    this.onClick = this.onClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.group.addEventListener('click', this.onClick);
    this.group.addEventListener('keydown', this.onKeydown);
  }

  disconnectedCallback() {
    this.group?.removeEventListener('click', this.onClick);
    this.group?.removeEventListener('keydown', this.onKeydown);
  }

  onClick(event) {
    const button = event.target.closest('button');
    if (button && this.buttons.includes(button)) this.select(button);
  }

  onKeydown(event) {
    const keys = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'];
    if (!keys.includes(event.key)) return;

    const current = this.buttons.indexOf(event.target.closest('button'));
    if (current === -1) return;

    event.preventDefault();

    let next;
    if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = this.buttons.length - 1;
    else {
      const step = event.key === 'ArrowLeft' || event.key === 'ArrowUp' ? -1 : 1;
      next = (current + step + this.buttons.length) % this.buttons.length;
    }

    this.buttons[next].focus();
    this.select(this.buttons[next]);
  }

  select(button) {
    for (const other of this.buttons) {
      other.setAttribute('aria-pressed', other === button ? 'true' : 'false');
    }

    for (const [name, value] of Object.entries(button.dataset)) {
      if (name === 'value') continue;
      const slot = this.querySelector(`[data-field="${name}"]`);
      if (slot) slot.textContent = value;
    }

    if (this.input && button.dataset.value !== undefined) {
      this.input.value = button.dataset.value;
    }
  }
}

if (!customElements.get('ss-picker')) {
  customElements.define('ss-picker', SSPicker);
}
